import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { CheckCircle2, XCircle, Wallet, Calculator } from "lucide-react";
import { STATUS_COLOR } from "./_helpers";

const DOCS = [
  { key: "pan_card", label: "PAN card" }, { key: "aadhaar", label: "Aadhaar" },
  { key: "salary_slips_3m", label: "Salary slips (3 months)" },
  { key: "bank_statement_6m", label: "Bank statement (6 months)" },
  { key: "form_16", label: "Form 16" }, { key: "photograph", label: "Photograph" },
  { key: "signed_form", label: "Signed loan form" }, { key: "address_proof", label: "Address proof" },
];

export default function FinanceTab() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState({ emi: 0, total_payable: 0, total_interest: 0 });
  const emptyForm = { booking_id: "", financier: "", financier_id: "", loan_amount: 0, tenure_months: 60,
                       roi: 9.5, down_payment: 0, application_date: today(),
                       docs_checklist: Object.fromEntries(DOCS.map((d) => [d.key, false])), remarks: "" };
  const [form, setForm] = useState(emptyForm);
  const [actionOpen, setActionOpen] = useState(null);
  const [actForm, setActForm] = useState({ action: "approved", sanction_letter_ref: "", disbursement_amount: 0, disbursement_date: today(), remarks: "" });

  const load = async () => {
    const [f, b, p] = await Promise.all([
      api.get("/dealership/finance-apps"),
      api.get("/dealership/bookings"),
      api.get("/dealership/finance-partners"),
    ]);
    setRows(f.data); setBookings(b.data.filter((x) => x.payment_type === "finance" || x.payment_mode === "finance")); setPartners(p.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (form.loan_amount > 0 && form.roi > 0 && form.tenure_months > 0) {
      api.get("/dealership/finance/emi-preview", {
        params: { principal: form.loan_amount, roi: form.roi, tenure_months: form.tenure_months },
      }).then((r) => setPreview(r.data));
    } else setPreview({ emi: 0, total_payable: 0, total_interest: 0 });
  }, [form.loan_amount, form.roi, form.tenure_months]);

  const save = async () => {
    if (!form.booking_id || !form.financier || form.loan_amount <= 0) return toast.error("Booking, financier & loan amount are required");
    try {
      const r = await api.post("/dealership/finance-apps", form);
      toast.success(`Loan ${r.data.finance_number} · EMI ${fmtINR(r.data.emi)}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openAction = (row, action) => {
    setActionOpen(row);
    setActForm({ action, sanction_letter_ref: "", disbursement_amount: row.loan_amount, disbursement_date: today(), remarks: "" });
  };
  const runAction = async () => {
    try {
      await api.post(`/dealership/finance-apps/${actionOpen.id}/action`, actForm);
      toast.success(`Loan ${actionOpen.finance_number} → ${actForm.action}`);
      setActionOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bookingLabel = (bid) => {
    if (!bid) return "— (no booking)";
    const b = bookings.find((x) => x.id === bid);
    return b ? `${b.booking_number} · ${b.customer?.first_name || ""} ${b.customer?.last_name || ""}` : String(bid).slice(0, 8);
  };
  const docsProgress = (dc) => {
    const total = Object.keys(dc || {}).length || DOCS.length;
    const done = Object.values(dc || {}).filter(Boolean).length;
    return `${done}/${total}`;
  };

  return (
    <div className="space-y-3" data-testid="finance-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-finance-btn">
          <Wallet className="w-3.5 h-3.5" /> New Loan Application
        </Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Loan#</th><th>Booking</th><th>Financier</th><th className="text-right">Loan</th><th className="text-right">EMI × months</th><th>Docs</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No loan applications yet.</td></tr>}
            {rows.map((f) => (
              <tr key={f.id} data-testid={`finance-row-${f.finance_number}`}>
                <td className="font-mono text-xs">{f.finance_number || f.id?.slice(0,8)}</td>
                <td className="text-xs">{bookingLabel(f.booking_id)}</td>
                <td className="text-xs">{f.financier || f.finance_partner_name || "—"}</td>
                <td className="text-right tabular-nums">{fmtINR(f.loan_amount)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(f.emi)} × {f.tenure_months}</td>
                <td className="text-[10px] font-mono text-muted-foreground">{docsProgress(f.docs_checklist)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[f.status] || ""}`}>{f.status}</span></td>
                <td className="text-right space-x-1">
                  {f.status === "applied" && <>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600" onClick={() => openAction(f, "approved")} data-testid={`approve-loan-${f.finance_number}`}>Approve</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => openAction(f, "rejected")}>Reject</Button>
                  </>}
                  {f.status === "approved" && <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-700" onClick={() => openAction(f, "disbursed")} data-testid={`disburse-loan-${f.finance_number}`}>Disburse</Button>}
                  {f.status === "disbursed" && <span className="text-[10px] font-mono uppercase text-emerald-700">✓ disbursed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">New Loan Application</SheetTitle>
            <SheetDescription className="sr-only">Capture loan details and required documents.</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Booking *</Label>
              <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="loan-booking"><SelectValue placeholder="Pick finance booking" /></SelectTrigger>
                <SelectContent>
                  {bookings.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Only finance-type bookings appear here</div>}
                  {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_number} · {b.customer?.first_name} · {fmtINR(b.on_road_price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Financier *</Label>
              <Select value={form.financier_id} onValueChange={(v) => {
                const p = partners.find((x) => x.id === v);
                setForm({ ...form, financier_id: v, financier: p?.name || "" });
              }}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="loan-partner"><SelectValue placeholder="Pick bank/NBFC" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.partner_type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Loan amount *</Label><Input type="number" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="loan-amount" /></div>
            <div><Label>Down payment</Label><Input type="number" value={form.down_payment} onChange={(e) => setForm({ ...form, down_payment: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            <div><Label>Rate of interest (% p.a.)</Label><Input type="number" step="0.1" value={form.roi} onChange={(e) => setForm({ ...form, roi: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="loan-roi" /></div>
            <div><Label>Tenure (months)</Label><Input type="number" value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="loan-tenure" /></div>
          </div>
          <div className="border rounded-sm p-3 bg-muted/30" data-testid="emi-preview">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Calculator className="w-3 h-3" /> EMI preview</div>
            <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
              <div><div className="text-muted-foreground">EMI/month</div><div className="font-heading text-lg tabular-nums" data-testid="emi-value">{fmtINR(preview.emi)}</div></div>
              <div><div className="text-muted-foreground">Total payable</div><div className="tabular-nums">{fmtINR(preview.total_payable)}</div></div>
              <div><div className="text-muted-foreground">Total interest</div><div className="tabular-nums">{fmtINR(preview.total_interest)}</div></div>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Documents checklist</div>
            <div className="grid grid-cols-2 gap-2">
              {DOCS.map((d) => (
                <label key={d.key} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`doc-${d.key}`}>
                  <input type="checkbox" checked={!!form.docs_checklist[d.key]}
                          onChange={(e) => setForm({ ...form, docs_checklist: { ...form.docs_checklist, [d.key]: e.target.checked } })} />
                  {d.label}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="loan-save">Create Application</Button>
        </SheetContent>
      </Sheet>

      <Sheet open={!!actionOpen} onOpenChange={(v) => !v && setActionOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">{actForm.action === "approved" ? "Approve Loan" : actForm.action === "rejected" ? "Reject Loan" : "Disburse Loan"}</SheetTitle>
            <SheetDescription className="sr-only">Take action on the loan application.</SheetDescription>
          </SheetHeader>
          {actionOpen && <>
            <div className="text-xs text-muted-foreground">Loan <span className="font-mono">{actionOpen.finance_number}</span> · {actionOpen.financier} · {fmtINR(actionOpen.loan_amount)}</div>
            {actForm.action === "approved" && (
              <div><Label>Sanction letter ref</Label><Input value={actForm.sanction_letter_ref} onChange={(e) => setActForm({ ...actForm, sanction_letter_ref: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="sanction-ref" /></div>
            )}
            {actForm.action === "disbursed" && <>
              <div><Label>Disbursement amount</Label><Input type="number" value={actForm.disbursement_amount} onChange={(e) => setActForm({ ...actForm, disbursement_amount: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="disb-amount" /></div>
              <div><Label>Disbursement date</Label><Input type="date" value={actForm.disbursement_date} onChange={(e) => setActForm({ ...actForm, disbursement_date: e.target.value })} className="rounded-sm mt-1" /></div>
            </>}
            <div><Label>Remarks</Label><Input value={actForm.remarks} onChange={(e) => setActForm({ ...actForm, remarks: e.target.value })} className="rounded-sm mt-1" /></div>
            <Button onClick={runAction} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="loan-action-run">
              Confirm {actForm.action}
            </Button>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
